import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import type { TFunction } from 'i18next';
import { useSaveHeader, type ChangeEntry } from './SaveHeaderContext';
import { defaultTopLevelDiff } from './defaultTopLevelDiff';

type Props<V extends Record<string, any>> = {
  tabKey: string;
  getChanges?: (values: V, initialValues: V, t: TFunction) => ChangeEntry[];
  successMessage?: string;
  disabled?: boolean;
};

// Must be rendered inside a <Formik> tree — reads the form state via
// useFormikContext() so callers don't have to hand-thread the formik bag.
export function SectionSaveRegistrar<V extends Record<string, any>>({
  tabKey,
  getChanges,
  successMessage,
  disabled,
}: Props<V>) {
  const { t } = useTranslation();
  const { registerActive, activeTab, showSuccess, showError } = useSaveHeader();
  const formik = useFormikContext<V>();
  const formikRef = useRef(formik);
  formikRef.current = formik;

  useEffect(() => {
    if (activeTab !== tabKey || disabled) {
      registerActive(null);
      return () => {};
    }
    const changes = getChanges
      ? getChanges(formik.values, formik.initialValues, t)
      : defaultTopLevelDiff(formik.values, formik.initialValues, t);

    const submit = async () => {
      const { current } = formikRef;
      current.setStatus({});
      await current.submitForm();
      const err = current.status?.error;
      if (err) {
        showError(typeof err === 'string' ? err : t('saveHeader.errorGeneric'));
        throw new Error(typeof err === 'string' ? err : 'submit failed');
      }
      showSuccess(successMessage);
    };

    registerActive({
      tabKey,
      submit,
      reset: () => formikRef.current.resetForm(),
      dirty: formik.dirty,
      isSubmitting: formik.isSubmitting,
      isValid: formik.isValid,
      changes,
    });

    return () => {
      registerActive(null);
    };
  }, [
    tabKey,
    activeTab,
    formik.dirty,
    formik.isSubmitting,
    formik.isValid,
    formik.values,
    formik.initialValues,
    disabled,
  ]);

  return null;
}
